import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { NgFor, NgIf } from '@angular/common';
import { LeagueWeek } from '../../../../models/LeagueWeek';
import { GolfEventField } from '../../../../models/golfeventfield';
import { PostScoreRequest } from '../../../../models/postScoreRequest';
import { GolfEventService } from '../../../../services/golf-event.service';
import { ApplicationService } from '../../../../services/application.service';
import { PlayerService } from '../../../../services/player.service';

@Component({
  selector: 'app-post-scramble-round',
  standalone: true,
  imports: [FormsModule, NgFor, NgIf],
  templateUrl: './post-scramble-round.component.html',
  styleUrl: './post-scramble-round.component.css'
})
export class PostScrambleRoundComponent implements OnChanges {
  constructor(
    private golfEventService:GolfEventService,
    private applicationService:ApplicationService,
    private playerService:PlayerService
  ) {}

  @Input() leagueWeek:LeagueWeek|null = null;
  @Input() loggedInPlayerId:number|null = null;
  @Input() paidPlayers:Array<GolfEventField> = [];
  @Input() useHandicaps:boolean = false;

  @Output() scoresPosted = new EventEmitter<void>();

  playerId:number|null = null;
  partnerPlayerId:number|null = null;
  partnerIsGuest:boolean = false;
  guestPartnerName:string = '';
  guestPartnerHandicapIndex:number|null = null;
  grossScore:number|null = null;

  warningMessage:string|null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loggedInPlayerId'] || changes['paidPlayers']) {
      this.defaultLoggedInPlayer();
    }

    // If handicaps turn on after the player was already defaulted, still warn.
    if (changes['useHandicaps'] && this.useHandicaps && this.playerId != null) {
      this.warnIfMissingHandicap(this.playerId);
    }
  }

  // Use the field list's playerId value as-is so the select matches (API bigints can arrive as strings).
  defaultLoggedInPlayer():void {
    if (this.playerId !== null || !this.loggedInPlayerId) return;

    const loggedIn = this.paidPlayers.find(
      p => p.playerId.toString() === this.loggedInPlayerId!.toString()
    );
    if (loggedIn) {
      this.playerId = loggedIn.playerId;
      this.warnIfMissingHandicap(this.playerId);
    }
  }

  get partnerOptions():Array<GolfEventField> {
    return this.paidPlayers.filter(p => p.playerId.toString() !== this.playerId?.toString());
  }

  onPlayerChange(playerId:number|null):void {
    this.playerId = playerId;
    if (this.partnerPlayerId != null && this.partnerPlayerId.toString() === this.playerId?.toString()) {
      this.partnerPlayerId = null;
    }
    this.warnIfMissingHandicap(playerId);
  }

  onPartnerChange(partnerId:number|null):void {
    this.partnerPlayerId = partnerId;
    this.warnIfMissingHandicap(partnerId);
  }

  setPartnerIsGuest(isGuest:boolean):void {
    this.partnerIsGuest = isGuest;
    if (isGuest) {
      this.partnerPlayerId = null;
    }
    else {
      this.guestPartnerName = '';
      this.guestPartnerHandicapIndex = null;
    }
  }

  async warnIfMissingHandicap(selectedPlayerId:number|null):Promise<void> {
    if (!this.useHandicaps || selectedPlayerId == null) return;

    const fieldPlayer = this.paidPlayers.find(p => p.playerId.toString() === selectedPlayerId.toString());
    const displayName = fieldPlayer
      ? `${fieldPlayer.firstName} ${fieldPlayer.lastName}`
      : 'That player';

    // Prefer a fresh player lookup; field lists can be stale, and "no index" is often stored as 0.
    let handicapIndex:number|null|undefined = fieldPlayer?.handicapIndex;
    try {
      const players = await this.playerService.searchPlayers(Number(selectedPlayerId));
      if (players.length > 0) {
        handicapIndex = players[0].handicapIndex;
      }
    } catch {
      // Fall back to whatever was on the field list.
    }

    if (!this.hasPostedHandicap(handicapIndex)) {
      this.showWarning(`${displayName} does not have a handicap index posted.`);
    }
  }

  showWarning(message:string):void {
    this.warningMessage = message;
  }

  dismissWarning():void {
    this.warningMessage = null;
  }

  // Matches player admin: blank/null/0 means no index has been posted.
  hasPostedHandicap(index:number|null|undefined):boolean {
    if (index === null || index === undefined) return false;
    if (String(index).trim() === '') return false;
    return Number(index) !== 0;
  }

  async postScore(form:NgForm):Promise<void> {
    if(!this.leagueWeek) return;

    form.control.markAllAsTouched();

    if(form.invalid) {
      alert("Form is invalid");
      return;
    }

    if(!this.playerId) {
      alert("Pick a player for the scramble.");
      return;
    }

    const score = Number(this.grossScore);

    if(!score || score <= 18) {
      alert("That score is too low and considered an invalid entry.");
      return;
    }

    const request:PostScoreRequest = {
      leagueWeekId: this.leagueWeek.id,
      playerId: Number(this.playerId),
      partnerPlayerId: null,
      guestPartnerName: null,
      guestPartnerHandicapIndex: null,
      teamId: null,
      score: score,
      email: null
    };

    if (this.partnerIsGuest) {
      const guestName = this.guestPartnerName.trim();
      if (!guestName) {
        alert("Enter a name for the guest partner.");
        return;
      }

      if (this.useHandicaps) {
        if (this.guestPartnerHandicapIndex === null || this.guestPartnerHandicapIndex === undefined || String(this.guestPartnerHandicapIndex).trim() === '') {
          alert("Enter a handicap index for the guest partner.");
          return;
        }
        request.guestPartnerHandicapIndex = Number(this.guestPartnerHandicapIndex);
      }

      request.guestPartnerName = guestName;
    }
    else {
      if(!this.partnerPlayerId) {
        alert("A scramble round needs two players.");
        return;
      }

      if(Number(this.playerId) === Number(this.partnerPlayerId)) {
        alert("Pick two different players for the scramble.");
        return;
      }

      request.partnerPlayerId = Number(this.partnerPlayerId);
    }

    this.applicationService.showGlobalSpinner();
    try {
      await this.golfEventService.postScores([request]);
      this.reset();
      this.scoresPosted.emit();
    } finally {
      this.applicationService.hideGlobalSpinner();
    }
  }

  reset():void {
    this.playerId = null;
    this.partnerPlayerId = null;
    this.partnerIsGuest = false;
    this.guestPartnerName = '';
    this.guestPartnerHandicapIndex = null;
    this.grossScore = null;
    this.defaultLoggedInPlayer();
  }
}
